import ResponsiveSection from '@/components/common/ResponsiveSection'
import { P } from '@/components/common/Typo'
import { useTranslate } from '@tolgee/react'
import {
  Card,
  ChevronDownIcon,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'oddle-shop-monorepo.design-system'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface NavigationItem {
  id: string
  name: string
  href: string
}

interface HorizontalNavigationProps {
  className?: string
  items: NavigationItem[]
  selectedItemId: string
  onClickItem: (event: React.MouseEvent<HTMLAnchorElement>, id: string) => void
}

interface NavigationState {
  isDropdownVisible: boolean
  displayedItems: NavigationItem[]
  hiddenItems: NavigationItem[]
}

const HorizontalNavigation: React.FC<HorizontalNavigationProps> = ({
  className,
  items = [],
  selectedItemId = '',
  onClickItem,
}) => {
  const { t } = useTranslate()
  const listRef = useRef<Record<string, HTMLElement | null>>({})
  const [state, setState] = useState<NavigationState>({
    isDropdownVisible: false,
    displayedItems: [],
    hiddenItems: [],
  })
  const [isOpenDropdown, setIsOpenDropdown] = useState(false)

  const updateNavigationItems = useCallback(() => {
    const container = listRef.current['container']
    if (!container) return

    const containerWidth = container.offsetWidth
    let currentWidth = 0
    const displayed: NavigationItem[] = []
    const hidden: NavigationItem[] = []

    items.forEach((item) => {
      const element = listRef.current[item.id]
      if (element) {
        const itemWidth = element.offsetWidth
        if (currentWidth + itemWidth <= containerWidth - 100) {
          displayed.push(item)
          currentWidth += itemWidth
        } else {
          hidden.push(item)
        }
      }
    })

    setState({
      isDropdownVisible: hidden.length > 0,
      displayedItems: displayed,
      hiddenItems: hidden,
    })
  }, [items])

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(updateNavigationItems)
    }

    window.addEventListener('resize', handleResize)
    updateNavigationItems()

    return () => window.removeEventListener('resize', handleResize)
  }, [updateNavigationItems])

  const selectedCategory = items.find((c) => c.id === selectedItemId)
  const labelText =
    selectedCategory &&
    state.hiddenItems.some((i: NavigationItem) => i.id === selectedCategory.id)
      ? selectedCategory.name
      : ''

  return (
    <Card
      className={cn('bg-container h-[70px] p-0', className)}
      id="product-section"
    >
      <ResponsiveSection className="m-0">
        <ResponsiveSection.Container
          ref={(ref) => {
            listRef.current['container'] = ref
          }}
          className={cn(
            'px-0',
            !state.isDropdownVisible ? 'overflow-x-auto' : ''
          )}
        >
          <div className="flex max-w-[calc(100vw-1.5rem)] flex-row lg:max-w-7xl lg:px-0">
            <div className="h-[70px] overflow-hidden pl-5">
              {items.map((cat) => (
                <span key={cat.id} className="cursor-pointer no-underline">
                  <a
                    href={cat.href}
                    ref={(ref) => {
                      listRef.current[cat.id] = ref
                    }}
                    className={cn(
                      'hover:text-primary inline-flex h-[70px] items-center whitespace-nowrap py-5',
                      cat.id === selectedItemId
                        ? 'text-button'
                        : 'text-headings'
                    )}
                    onClick={(e) => onClickItem(e, cat.id)}
                  >
                    <P
                      className={cn(
                        'm-0 max-w-full px-4 py-2.5 text-base font-bold text-inherit',
                        cat.id === selectedItemId
                          ? 'bg-button text-button-text rounded-lg shadow-sm hover:opacity-80'
                          : 'hover:text-button'
                      )}
                    >
                      {cat.name}
                    </P>
                  </a>
                </span>
              ))}
            </div>
            {state.isDropdownVisible && (
              <div className="ml-auto">
                <DropdownMenu
                  open={isOpenDropdown}
                  onOpenChange={setIsOpenDropdown}
                  modal={false}
                >
                  <DropdownMenuTrigger className="hover:text-primary flex h-full items-center gap-2 whitespace-nowrap px-4 text-base font-bold outline-none">
                    {labelText !== '' ? labelText : t('More')}
                    <div className={isOpenDropdown ? 'rotate-180' : ''}>
                      <ChevronDownIcon className="h-4 w-4" />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="border-lines-and-borders max-h-[50vh] max-w-[200px] overflow-y-auto rounded-xl">
                    {state.hiddenItems.map((item: NavigationItem) => (
                      <DropdownMenuItem
                        asChild
                        className="text-body cursor-pointer px-5 py-2 text-base font-bold"
                        key={item.id}
                      >
                        <a
                          href={item.href}
                          onClick={async (e) => {
                            await onClickItem(e, item.id)
                            setIsOpenDropdown(false)
                          }}
                        >
                          {item.name}
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </ResponsiveSection.Container>
      </ResponsiveSection>
    </Card>
  )
}

export default HorizontalNavigation
